import { useSyncExternalStore } from 'react';

/**
 * Tiny global store for the "log in to play" advisory modal.
 *
 * Reusable from anywhere (deep table components, lobby, tournament views)
 * without threading a context/provider: components call `promptLogin()` to
 * surface the modal, and a single <LoginRequiredModal /> mounted at the app
 * root renders it. Used wherever a guest attempts a play action (take a seat,
 * join a table, register for a tournament, …).
 */
export interface LoginPromptState {
  open: boolean;
  title: string;
  message: string;
  dismissLabel: string;
}

const DEFAULTS = {
  title: 'Login to play',
  message:
    "You're browsing as a guest. Login or create an account to take a seat and play — you can keep watching in the meantime.",
  dismissLabel: 'Keep watching'
};

let state: LoginPromptState = { open: false, ...DEFAULTS };
const listeners = new Set<() => void>();

const emit = () => {
  listeners.forEach((listener) => listener());
};

/**
 * Open the advisory modal. Optionally override the copy for a specific action
 * (e.g. "Login to register" for tournaments).
 */
export function promptLogin(opts?: {
  title?: string;
  message?: string;
  dismissLabel?: string;
}): void {
  state = {
    open: true,
    title: opts?.title ?? DEFAULTS.title,
    message: opts?.message ?? DEFAULTS.message,
    dismissLabel: opts?.dismissLabel ?? DEFAULTS.dismissLabel
  };
  emit();
}

export function closeLoginPrompt(): void {
  if (!state.open) return;
  state = { ...state, open: false };
  emit();
}

function subscribe(callback: () => void): () => void {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

function getSnapshot(): LoginPromptState {
  return state;
}

/** Subscribe a component to the login-prompt state. */
export function useLoginPrompt(): LoginPromptState {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
